import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";

const SESSION_KEY = "eq_chat_session";

export default function Chatbot() {
  const { settings } = useSettings();
  const config = settings?.chatbot_config || {};
  const greeting = config.greeting || "Hi! I'm EQtech's AI Assistant. How can I help you today?";
  const quickReplies = config.quick_replies || [];
  const primaryColor = config.primary_color || "#0EA5E9";

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: greeting }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "", email: "", course: "" });
  const [savedLead, setSavedLead] = useState(false);
  const scrollRef = useRef(null);

  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, sessionId);
  }

  useEffect(() => {
    // Update greeting when settings change (first message)
    setMessages((m) => (m.length && m[0].role === "assistant" ? [{ role: "assistant", content: greeting }, ...m.slice(1)] : m));
  }, [greeting]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const sendText = async (text) => {
    if (!text || streaming) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data:\s?/, "");
          if (line === "[DONE]") continue;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + line };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm having trouble responding. Please try the contact form." };
        return copy;
      });
    } finally { setStreaming(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    await sendText(text);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.phone) return;
    try {
      await api.post("/chat/save-lead", { ...lead, source: "chatbot" });
      setSavedLead(true);
      setMessages((m) => [...m, { role: "assistant", content: `Thank you ${lead.name}! Our counselor will call you at ${lead.phone} shortly.` }]);
    } catch (err) {
      console.error("Chatbot lead save failed:", err);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-neutral-950 hover:bg-sky-500 shadow-xl flex items-center justify-center transition-all hover:scale-110"
        data-testid="chatbot-toggle"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageSquare className="w-6 h-6 text-white" />}
        {!open && <span className="absolute top-0 right-0 w-3 h-3 bg-sky-500 rounded-full pulse-dot"></span>}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-sm h-[520px] bg-white rounded-2xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden" data-testid="chatbot-panel">
          <div className="bg-neutral-950 text-white p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-sky-500/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-sky-400" />
            </div>
            <div>
              <div className="font-display font-bold text-sm">EQtech AI Assistant</div>
              <div className="text-xs text-neutral-400 flex items-center gap-2"><span className="w-2 h-2 bg-green-400 rounded-full"></span> Online</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-neutral-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                  m.role === "user" ? "bg-neutral-900 text-white rounded-br-sm" : "bg-white border border-neutral-200 text-neutral-800 rounded-bl-sm"
                }`}>
                  {m.content || (streaming && i === messages.length - 1 ? "..." : "")}
                </div>
              </div>
            ))}
            {messages.length === 1 && quickReplies.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="chatbot-quick-replies">
                {quickReplies.map((qr, i) => (
                  <button key={i} onClick={() => sendText(qr)} disabled={streaming}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-sky-200 text-sky-700 hover:bg-sky-50 transition-colors">
                    {qr}
                  </button>
                ))}
              </div>
            )}
          </div>

          {!savedLead && messages.length >= 4 && (
            <form onSubmit={submitLead} className="bg-sky-50 border-t border-sky-100 p-3 space-y-2" data-testid="chatbot-lead-form">
              <div className="text-xs font-semibold text-neutral-700">Share your details for a callback:</div>
              <div className="flex gap-2">
                <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Name" className="flex-1 text-xs px-3 py-1.5 rounded-full border border-sky-200 bg-white" data-testid="chatbot-lead-name" />
                <input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Phone" className="flex-1 text-xs px-3 py-1.5 rounded-full border border-sky-200 bg-white" data-testid="chatbot-lead-phone" />
              </div>
              <button type="submit" className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full py-1.5 text-xs font-semibold transition-colors" data-testid="chatbot-lead-submit">Request Callback</button>
            </form>
          )}

          <form onSubmit={send} className="border-t border-neutral-200 p-3 flex gap-2 bg-white" data-testid="chatbot-input-form">
            <input
              value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message..." disabled={streaming}
              className="flex-1 px-4 py-2 rounded-full border border-neutral-200 text-sm focus:outline-none focus:border-sky-500"
              data-testid="chatbot-input"
            />
            <button type="submit" disabled={streaming} className="w-10 h-10 rounded-full bg-neutral-900 hover:bg-sky-500 text-white flex items-center justify-center disabled:opacity-50 transition-colors" data-testid="chatbot-send">
              <Send className="w-4 h-4" />
            </button>
          </form>

          {settings?.whatsapp_auto?.business_number && (
            <a
              href={`https://wa.me/${settings.whatsapp_auto.business_number.replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Hi, I was chatting with the bot on your website and want to speak with a counselor.")}`}
              target="_blank" rel="noreferrer"
              data-testid="chatbot-whatsapp-handover"
              className="block text-center text-xs py-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold"
            >
              💬 Continue on WhatsApp →
            </a>
          )}
        </div>
      )}
    </>
  );
}
